// Listing-request endpoint functions. Async, call `sellerApi`, throw `AppError`
// (mapped by the interceptor), and return DTOs — the parser runs in the
// mutation hook. The create call is multipart/form-data: the title-deed file
// plus the collected wizard fields. FormData is built here so the mutation and
// container stay clean; axios sets the multipart boundary automatically.
import { sellerApi } from '@core/api/httpClient'
import { toE164Mobile, toEmiratesIdDigits } from '../utils/ownerContactUtils'
import { listingRequestEndpointPaths } from './listingRequestEndpointPaths'
import type {
  ApiEnvelope,
  InitiateSigningDto,
  ListingRequestDto,
  ListingRequestDetailDto,
  ListingRequestEventDto,
  ListingRequestListDto,
  ListingRequestSummaryDto,
  PaginatedEnvelope,
  PropertyAgreementDto,
  ShareSignatoryLinkDto,
} from './listingRequestContracts'
import type {
  CreateListingRequestPayload,
  DeclineAgreementSignaturePayload,
  OwnerDraft,
  RespondToPricePayload,
  ReviseListingRequestPayload,
  WithdrawListingRequestPayload,
} from '../types/listingRequestTypes'

// Query params accepted by the paginated list endpoint. `status` is omitted when
// empty (the server treats a missing filter as "all").
export interface GetListingRequestsParams {
  page: number
  limit: number
  status?: string
}

// Optional fields are appended only when non-empty — the server validates
// present fields, so sending an empty string would fail format checks.
function appendOptional(formData: FormData, key: string, value: string) {
  const trimmed = value.trim()
  if (trimmed) formData.append(key, trimmed)
}

// Owners travel as a JSON string field (an array can't ride multipart cleanly);
// the server parses + validates it. Percentage is coerced to a number here; the
// mobile number is normalised to E.164 (the tel input renders it spaced) and the
// Emirates ID to bare digits (the seller may type the card's dashes).
function serializeOwners(owners: OwnerDraft[]): string {
  return JSON.stringify(
    owners.map((owner) => ({
      fullName: owner.fullName.trim(),
      ownershipPercentage: Number(owner.ownershipPercentage),
      email: owner.email.trim(),
      mobileNumber: toE164Mobile(owner.mobileNumber),
      emiratesId: toEmiratesIdDigits(owner.emiratesId),
      ownerType: owner.ownerType,
      isPrimary: owner.isPrimary,
    })),
  )
}

// Shared multipart serializer for create + revise. The field set is identical;
// the only difference is `titleDeed`, which is required on create and optional
// on revise (the server keeps the existing deed when it's omitted).
function toFormData(payload: CreateListingRequestPayload | ReviseListingRequestPayload): FormData {
  const formData = new FormData()
  if (payload.titleDeed) formData.append('titleDeed', payload.titleDeed)
  // Only ever sent alongside `isRented: true` — the server rejects an agreement
  // attached to a property the seller says is not rented.
  if (payload.rentalAgreement) formData.append('rentalAgreement', payload.rentalAgreement)
  formData.append('ownershipRole', payload.ownershipRole)
  // Required, so appended unconditionally — `appendOptional` would silently drop it
  // and the request would land with a null type that can never be promoted.
  formData.append('propertyType', payload.propertyType)
  formData.append('isRented', String(payload.isRented))
  formData.append('certificateNumber', payload.certificateNumber.trim())
  formData.append('propertyName', payload.propertyName.trim())
  formData.append('askingPrice', payload.askingPrice.trim())
  formData.append('currencyCode', payload.currencyCode)
  formData.append('owners', serializeOwners(payload.owners))
  appendOptional(formData, 'totalAreaSqm', payload.totalAreaSqm)
  appendOptional(formData, 'propertyNumber', payload.propertyNumber)
  appendOptional(formData, 'buildingName', payload.buildingName)
  appendOptional(formData, 'locationArea', payload.locationArea)
  appendOptional(formData, 'addressLine1', payload.addressLine1)
  appendOptional(formData, 'city', payload.city)
  appendOptional(formData, 'countryId', payload.countryId)
  appendOptional(formData, 'plotNumber', payload.plotNumber)
  appendOptional(formData, 'municipalityNumber', payload.municipalityNumber)
  return formData
}

export async function createListingRequest(
  payload: CreateListingRequestPayload,
): Promise<ListingRequestDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDto>>(
    listingRequestEndpointPaths.LISTING_REQUESTS,
    toFormData(payload),
  )
  return data.data
}

// ── Phase B seller actions ────────────────────────────────────────────────
// All three return the full refreshed detail (same shape as GET :id), so the
// mutation hooks run `parseListingRequestDetail` on the result.

// Accept / reject the admin's proposed price. JSON body; not a financial write.
export async function respondToListingRequestPrice(
  id: string,
  payload: RespondToPricePayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequestPriceResponse(id),
    payload,
  )
  return data.data
}

// Withdraw the seller's own request. Idempotent server-side.
export async function withdrawListingRequest(
  id: string,
  payload: WithdrawListingRequestPayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequestWithdraw(id),
    payload,
  )
  return data.data
}

// Edit + resubmit after a revision request. Multipart PUT on the detail path;
// the title deed is optional (existing deed kept when omitted).
export async function reviseListingRequest(
  id: string,
  payload: ReviseListingRequestPayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.put<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequest(id),
    toFormData(payload),
  )
  return data.data
}

// Paginated list of the seller's own requests. Unwraps the paginated envelope
// into rows + pagination meta for the parser.
export async function getListingRequests(
  params: GetListingRequestsParams,
): Promise<ListingRequestListDto> {
  const { data } = await sellerApi.get<PaginatedEnvelope<ListingRequestSummaryDto>>(
    listingRequestEndpointPaths.LISTING_REQUESTS,
    {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.status ? { status: params.status } : {}),
      },
    },
  )
  return { items: data.data, pagination: data.pagination }
}

export async function getListingRequest(id: string): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.get<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequest(id),
  )
  return data.data
}

export async function getListingRequestTimeline(
  id: string,
): Promise<ListingRequestEventDto[]> {
  const { data } = await sellerApi.get<ApiEnvelope<ListingRequestEventDto[]>>(
    listingRequestEndpointPaths.listingRequestTimeline(id),
  )
  return data.data
}

export async function getListingRequestAgreements(
  id: string,
): Promise<PropertyAgreementDto[]> {
  const { data } = await sellerApi.get<ApiEnvelope<PropertyAgreementDto[]>>(
    listingRequestEndpointPaths.listingRequestAgreements(id),
  )
  return data.data
}

// ── UAE Pass agreement signing ────────────────────────────────────────────

// Start a signing session for one signatory; returns the URL to redirect to.
export async function initiateAgreementSignature(
  id: string,
  agreementId: string,
  signatoryId: string,
): Promise<InitiateSigningDto> {
  const { data } = await sellerApi.post<ApiEnvelope<InitiateSigningDto>>(
    listingRequestEndpointPaths.agreementSignInitiate(id, agreementId, signatoryId),
  )
  return data.data
}

// Mint a secure public signing-page link for one signatory, to share with the
// owner (e.g. a co-owner who signs on their own device).
export async function shareAgreementSignatureLink(
  id: string,
  agreementId: string,
  signatoryId: string,
): Promise<ShareSignatoryLinkDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ShareSignatoryLinkDto>>(
    listingRequestEndpointPaths.agreementShareLink(id, agreementId, signatoryId),
  )
  return data.data
}

// Finalise a signing session after the signer returns from UAE Pass. Returns
// the refreshed agreement (parsed via `parseAgreement` in the hook).
export async function completeAgreementSignature(
  id: string,
  agreementId: string,
  signatoryId: string,
): Promise<PropertyAgreementDto> {
  const { data } = await sellerApi.post<ApiEnvelope<PropertyAgreementDto>>(
    listingRequestEndpointPaths.agreementSignComplete(id, agreementId, signatoryId),
  )
  return data.data
}

// Decline to sign an agreement (optional reason).
export async function declineAgreementSignature(
  id: string,
  agreementId: string,
  signatoryId: string,
  payload: DeclineAgreementSignaturePayload,
): Promise<PropertyAgreementDto> {
  const { data } = await sellerApi.post<ApiEnvelope<PropertyAgreementDto>>(
    listingRequestEndpointPaths.agreementSignDecline(id, agreementId, signatoryId),
    payload,
  )
  return data.data
}
